/**
 * Types out the lines of every .r2o-term, one character at a time, and starts
 * over once the session is finished. A .r2o-term-static window is left as is.
 *
 * The lines are static markup, so without JavaScript or with reduced motion
 * the finished session simply stays on screen.
 */
import '../css/terminal.css';

const TYPING_SPEED = 18;
const LINE_GAP = 250;
const REST_BEFORE_RESET = 2200;

const wait = ( ms ) => new Promise( ( resolve ) => setTimeout( resolve, ms ) );

/**
 * @param {HTMLElement} body The element holding the .r2o-term-line rows.
 */
async function play( body ) {
	// Each line as its class and a list of [className, text] segments.
	const lines = [ ...body.querySelectorAll( '.r2o-term-line' ) ].map(
		( line ) => ( {
			lineClass: line.className,
			segments: [ ...line.childNodes ].map( ( node ) => [
				node.className ?? '',
				node.textContent,
			] ),
		} )
	);

	// The finished session stays underneath, hidden, and holds the window at
	// its final height while the lines are typed over it.
	const ghost = document.createElement( 'div' );
	ghost.className = 'r2o-term-ghost';
	ghost.append( ...body.childNodes );

	const screen = document.createElement( 'div' );
	screen.className = 'r2o-term-screen';
	body.append( ghost, screen );

	for (;;) {
		screen.replaceChildren();

		for ( const { lineClass, segments } of lines ) {
			const row = document.createElement( 'div' );
			row.className = lineClass;

			const cursor = document.createElement( 'span' );
			cursor.className = 'r2o-term-cursor';
			cursor.textContent = '▋';
			row.append( cursor );
			screen.append( row );

			for ( const [ className, text ] of segments ) {
				const span = document.createElement( 'span' );
				span.className = className;
				row.insertBefore( span, cursor );

				for ( const char of text ) {
					span.textContent += char;
					await wait( TYPING_SPEED );
				}
			}

			cursor.remove();
			await wait( LINE_GAP );
		}

		await wait( REST_BEFORE_RESET );
	}
}

if ( ! window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches ) {
	document
		.querySelectorAll( '.r2o-term:not(.r2o-term-static) .r2o-term-body' )
		.forEach( play );
}
